/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from "classnames";
import dayjs from "dayjs";
import React from "react";

import styles from "./Grid.module.scss";

export interface Props {
  gridStart: dayjs.Dayjs;
  onSelect: (m: dayjs.Dayjs) => void;
  selectionStart: dayjs.Dayjs | null;
  selectionEnd: dayjs.Dayjs | null;
  allowPast?: boolean;
  earliestAllowed?: dayjs.Dayjs;
  firstDayOfWeek?: number; // 1 is Monday, 7 is Sunday
  noClear?: boolean;
}

const weekdaysShort = (day: number): string => {
  return ["日", "月", "火", "水", "木", "金", "土"][day % 7];
};

class Grid extends React.PureComponent<Props> {
  public static defaultProps = {
    allowPast: false,
    earliestAllowed: dayjs(),
    firstDayOfWeek: 1,
    noClear: false,
  };

  public renderLabels() {
    const labels: string[] = [];
    for (let i = 0; i < 7; i++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      labels.push(weekdaysShort((this.props.firstDayOfWeek! + i) % 7));
    }

    return (
      <div className={styles.dayLabels}>
        {labels.map((label: string) => (
          <div key={label} className={styles.dayLabel}>
            {label}
          </div>
        ))}
      </div>
    );
  }

  public onSelect = (day: dayjs.Dayjs) => () => {
    this.props.onSelect(day);
  };

  public renderRow(rowData: any[], rowIndex: number) {
    return (
      <div key={rowIndex} className={styles.dateRow}>
        {rowData.map((item: any, index) => (
          <div
            key={`${item.label}-${index}`}
            className={classNames([
              styles.dateItem,
              ...item.state.map((s: any) => styles[s]),
            ])}
            onClick={
              item.isPlaceholder || item.state.includes("disabled")
                ? undefined
                : this.onSelect(item.day)
            }
          >
            {!item.isPlaceholder && (
              <div className={styles.dateLabel}>{item.label}</div>
            )}
          </div>
        ))}
      </div>
    );
  }

  public renderGrid() {
    const gridStart = this.props.gridStart.clone().startOf("month");
    const { selectionStart, selectionEnd } = this.props;

    const flatRows: any[] = [];
    for (
      let day = gridStart.clone();
      day.isSame(gridStart, "month");
      day = day.add(1, "day")
    ) {
      const dayState = [];
      if (
        !this.props.allowPast &&
        this.props.earliestAllowed &&
        day.isBefore(dayjs(this.props.earliestAllowed.format()), "day")
      ) {
        dayState.push("disabled");
      }
      if (
        (selectionStart && day.isSame(dayjs(selectionStart.format()), "day")) ||
        (selectionEnd && day.isSame(dayjs(selectionEnd.format()), "day"))
      ) {
        dayState.push("end");
      }
      if (
        selectionStart &&
        selectionEnd &&
        day.isAfter(dayjs(selectionStart.format()), "day") &&
        day.isBefore(dayjs(selectionEnd.format()), "day")
      ) {
        dayState.push("intermediate");
      }
      if (day.isSame(dayjs(), "day")) {
        dayState.push(" today");
      }
      flatRows.push({
        day: day.clone(),
        isPlaceholder: false,
        label: day.date(),
        state: dayState,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const deltaStart = gridStart.isoWeekday() - this.props.firstDayOfWeek!;
    for (let i = 0; i < deltaStart; i++) {
      flatRows.unshift({
        day: null,
        isPlaceholder: true,
        label: "",
        state: ["disabled"],
      });
    }

    for (let i = gridStart.daysInMonth() + deltaStart; i < 42; i++) {
      flatRows.push({
        day: null,
        isPlaceholder: true,
        label: "",
        state: ["disabled"],
      });
    }

    const rows: any[][] = [];
    for (let row = 0; row < 6; row++) {
      rows.push([]);
      for (let col = 0; col < 7; col++) {
        rows[row].push(flatRows[row * 7 + col]);
      }
    }

    return (
      <div className="dateGrid">
        {rows.map((row: any[], index) => this.renderRow(row, index))}
      </div>
    );
  }

  public render() {
    return (
      <div className={styles.root}>
        {" "}
        <div className={styles.month}>
          {this.props.gridStart.format("YYYY年 MMM")}
        </div>
        {this.renderLabels()}
        {this.renderGrid()}
      </div>
    );
  }
}

export default Grid;
